import React, { useState, useEffect, useRef } from "react";
import { C, R, S } from "../theme";
import { formatStamp } from "../dateutil";
import { initialsOf } from "../export";
import { getSticky, setSticky } from "../sticky";
import { useI18n } from "../i18n";

// Standardized, compliance-grade operator sign-off used identically across ALL
// log types. Pure typed cursive signature (no canvas): the signature auto-fills
// from the operator name in a "Dancing Script" font, stays in real-time sync as
// the name is typed, and can be edited. Emits { signature, name, initials,
// complete } where `signature` is the typed text (legally binding under UK
// eIDAS / Electronic Communications Act 2000 with printed name + initials + stamp).

const SIG_FONT = '"Dancing Script", cursive';

export default function SignOff({ defaultName = "", onChange, title, useSticky = true, persistSticky = true }) {
  const { t } = useI18n();
  const heading = title != null ? title : t("signoff.operatorSignOff");
  const stickyName = useSticky ? getSticky("name") : "";
  const stickyIni = useSticky ? getSticky("initials") : "";
  const initName = stickyName || defaultName || "";
  const [name, setName] = useState(initName);
  const [initials, setInitials] = useState(stickyIni || initialsOf(initName));
  const [signature, setSignature] = useState(initName);
  const [initialsEdited, setInitialsEdited] = useState(!!stickyIni);
  const [now, setNow] = useState(new Date());
  const mounted = useRef(false);

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 20000); return () => clearInterval(t); }, []);

  const persist = (nm, ini) => { if (persistSticky) { setSticky("name", nm); setSticky("initials", ini); } };

  const fire = (v) => onChange && onChange({ ...v, complete: !!(String(v.signature).trim() && String(v.name).trim() && String(v.initials).trim()) });

  // Emit the initial (pre-filled) value once so parent forms start valid when sticky/default is present.
  useEffect(() => { if (!mounted.current) { mounted.current = true; fire({ signature, name, initials }); } });

  const onName = (v) => {
    setName(v);
    setSignature(v); // signature mirrors the operator name in real time
    if (initialsEdited) { persist(v, initials); fire({ signature: v, name: v, initials }); return; }
    const ini = initialsOf(v);
    setInitials(ini);
    persist(v, ini);
    fire({ signature: v, name: v, initials: ini });
  };
  const onInitials = (v) => {
    setInitialsEdited(true);
    const iv = v.toUpperCase().slice(0, 6);
    setInitials(iv);
    persist(name, iv);
    fire({ signature, name, initials: iv });
  };
  const onSignature = (v) => { setSignature(v); fire({ signature: v, name, initials }); };

  const fieldWrap = { background: C.card2, border: `1px solid ${C.borderStrong}`, borderRadius: R.md, color: C.text, fontSize: 16, padding: `0 ${S.md}px`, minHeight: 50, width: "100%", outline: "none" };
  const lbl = { color: C.sub, fontSize: 13, fontWeight: 700, marginBottom: S.xs, textTransform: "uppercase", letterSpacing: 0.4 };

  return (
    <div data-testid="signoff-widget" style={{ marginTop: S.sm }}>
      <div style={{ color: C.amber, fontSize: 14, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: S.sm }}>{heading}</div>

      <div style={{ display: "flex", gap: S.sm, marginBottom: S.md }}>
        <div style={{ flex: 2 }}>
          <div style={lbl}>{t("signoff.printedName")} *</div>
          <input data-testid="signoff-name" value={name} onChange={(e) => onName(e.target.value)} placeholder={t("signoff.fullNamePlaceholder")} style={fieldWrap} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={lbl}>{t("signoff.initials")} *</div>
          <input data-testid="signoff-initials" value={initials} onChange={(e) => onInitials(e.target.value)} placeholder={t("signoff.initialsPlaceholder")} style={{ ...fieldWrap, textTransform: "uppercase", fontWeight: 800, letterSpacing: 1 }} />
        </div>
      </div>

      <div style={lbl}>{t("signoff.signature")} *</div>
      <div data-testid="signoff-preview" style={{ background: "#ffffff", borderRadius: R.md, border: `2px dashed ${C.borderStrong}`, minHeight: 88, display: "flex", alignItems: "center", padding: `0 ${S.lg}px`, overflow: "hidden" }}>
        {String(signature).trim()
          ? <span style={{ fontFamily: SIG_FONT, color: "#0f172a", fontSize: 44, lineHeight: 1.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{signature}</span>
          : <span style={{ color: "#94A3B8", fontSize: 14 }}>{t("signoff.sigPlaceholder")}</span>}
      </div>
      <input data-testid="signoff-typed-input" value={signature} onChange={(e) => onSignature(e.target.value)} placeholder={t("signoff.typeSignature")} style={{ ...fieldWrap, marginTop: S.sm }} />
      <div style={{ color: C.muted, fontSize: 12, marginTop: S.xs }}>{t("signoff.autoFillHint")}</div>

      <div data-testid="signoff-timestamp" style={{ color: C.muted, fontSize: 12, marginTop: S.sm }}>
        {t("signoff.autoStamped")} <b style={{ color: C.sub }}>{formatStamp(now)}</b>
      </div>
    </div>
  );
}

// Read-only audit block for summary modals & detail screens.
// Renders legacy hand-drawn signatures (data-URI images) OR typed cursive signatures.
export function AuditBlock({ signature, name, initials, submittedAt, label }) {
  const { t } = useI18n();
  const blockLabel = label != null ? label : t("signoff.auditLabel");
  const ini = initialsOf(name, initials);
  const isImage = signature && String(signature).startsWith("data:");
  return (
    <div data-testid="audit-block" style={{ background: C.card2, borderRadius: R.md, padding: S.md, border: `1px solid ${C.borderStrong}` }}>
      <div style={{ color: C.amber, fontSize: 12, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: S.sm }}>{blockLabel}</div>
      {isImage
        ? <img data-testid="audit-signature" src={signature} alt="operator signature" style={{ width: "100%", maxHeight: 130, objectFit: "contain", background: "#fff", borderRadius: 6 }} />
        : signature
          ? <div data-testid="audit-signature" style={{ background: "#fff", borderRadius: 6, padding: `${S.sm}px ${S.md}px`, minHeight: 60, display: "flex", alignItems: "center", overflow: "hidden" }}><span style={{ fontFamily: '"Dancing Script", cursive', color: "#0f172a", fontSize: 38, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{signature}</span></div>
          : <div style={{ color: C.muted, fontStyle: "italic", fontSize: 13, padding: `${S.sm}px 0` }}>{t("signoff.noSignature")}</div>}
      <div style={{ marginTop: S.sm, display: "grid", gap: 6 }}>
        <div style={{ color: C.text, fontSize: 14 }}><span style={{ color: C.muted }}>{t("signoff.signedBy")}</span> <b>{name || "-"}</b></div>
        <div style={{ color: C.text, fontSize: 14 }}><span style={{ color: C.muted }}>{t("signoff.initialsLabel")}</span> <b>{ini || "-"}</b></div>
        <div style={{ color: C.text, fontSize: 14 }}><span style={{ color: C.muted }}>{t("signoff.submittedOn")}</span> <b>{formatStamp(submittedAt)}</b></div>
      </div>
    </div>
  );
}
